import React, { useCallback, useEffect, useRef } from 'react';
import { ActionIcon, Box, Button, Group, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import * as TablerIcons from '@tabler/icons-react';
import {
  buttonAppearance,
  ctaButtonClasses,
  startRippleOn,
} from '../../blocks/web/shared/buttonAppearance';
import { useSelectedItemProps } from './useSelectedItemProps';
import { usePuckSiteTheme, sitePrimaryColor } from '../PuckSiteThemeContext';
import styles from './ButtonPreview.module.css';

/** Durées du rejeu : le survol s'installe, puis le clic part. */
const HOVER_MS = 650;
const PRESS_MS = 220;

/** Libellé riche dépouillé — l'aperçu affiche du texte, pas du balisage. */
function plainText(html) {
  const raw = String(html || '');
  if (!raw) return '';
  const text = typeof DOMParser === 'undefined'
    ? raw.replace(/<[^>]+>/g, ' ')
    : new DOMParser().parseFromString(raw, 'text/html').body.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Aperçu interactif d'un bouton, en tête de ses réglages.
 *
 * Les états d'un bouton — survol, focus, animation de clic — étaient invisibles
 * depuis la sidebar : il fallait viser le bouton sur le canvas, où un clic sert
 * d'abord à placer le curseur dans le libellé. L'aperçu est un vrai bouton, avec
 * les vraies valeurs résolues : on le survole, on l'atteint au clavier, on le
 * clique. Le bouton « rejouer » enchaîne survol puis clic, parce qu'une
 * animation de 150 ms se rate à la souris.
 *
 * Il ne rend pas le bloc : celui-ci lit le contexte Puck et le thème de l'iframe
 * du canvas, qui n'ont rien à faire dans la sidebar (§4 du README).
 */
export default function ButtonPreview({ buttonProps, label }) {
  const selected = useSelectedItemProps();
  const siteTheme = usePuckSiteTheme();
  const itemProps = buttonProps || selected.itemProps || {};
  const nodeRef = useRef(null);
  const timersRef = useRef([]);

  // Un rejeu en cours quand la sidebar change de bloc laisserait la classe
  // posée sur un bouton qui n'est plus le même.
  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  const appearance = buttonAppearance(itemProps);
  const isRipple = appearance.pressAnimation === 'ripple';

  const startRipple = useCallback((event) => {
    startRippleOn(nodeRef.current, event);
  }, []);

  /** Survol pendant HOVER_MS, puis l'animation de clic. */
  const replay = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;
    clearTimers();
    node.classList.remove(ctaButtonClasses.forceHover, ctaButtonClasses.pressActive);
    // Reflow forcé : deux rejeus rapprochés doivent repartir de zéro.
    void node.offsetWidth;
    node.classList.add(ctaButtonClasses.forceHover);

    const at = (ms, fn) => timersRef.current.push(setTimeout(fn, ms));
    at(HOVER_MS, () => {
      // L'ondulation part du centre. Elle n'est déclenchée que si le bouton la
      // porte : la classe qui la dessine n'est pas conditionnée à `.ripple`,
      // un appel inconditionnel en ferait apparaître une partout.
      if (isRipple) {
        const rect = node.getBoundingClientRect();
        startRippleOn(node, {
          clientX: rect.left + rect.width / 2,
          clientY: rect.top + rect.height / 2,
        });
      }
      node.classList.add(ctaButtonClasses.pressActive);
    });
    at(HOVER_MS + PRESS_MS, () => {
      node.classList.remove(ctaButtonClasses.pressActive);
    });
    at(HOVER_MS + PRESS_MS + 200, () => {
      node.classList.remove(ctaButtonClasses.forceHover);
    });
  }, [clearTimers, isRipple]);

  const Icon = itemProps.icon ? TablerIcons[itemProps.icon] : null;
  const iconNode = Icon
    ? <Icon size={18} stroke={1.5} color={itemProps.iconColor || undefined} />
    : null;
  const iconOnly = appearance.iconSide === 'only' && !!iconNode;
  const text = plainText(label !== undefined ? label : itemProps.label) || 'Bouton';

  // Sans couleur réglée, le canvas peint le bouton dans la couleur principale
  // du site : les teintes du thème ne sont pas en CSS variables dans la
  // sidebar, on passe donc la valeur elle-même.
  const siteColor = sitePrimaryColor(siteTheme);

  return (
    <Box className={styles.frame}>
      <Group justify="space-between" align="center" mb={6} wrap="nowrap">
        <Text size="10px" c="dimmed" tt="uppercase" fw={600}>Aperçu</Text>
        <ActionIcon
          size="sm"
          variant="subtle"
          color="gray"
          title="Rejouer le survol et le clic"
          aria-label="Rejouer le survol et le clic"
          onClick={replay}
        >
          <FontAwesomeIcon icon={icon({ name: 'play', style: 'solid' })} style={{ fontSize: 12 }} />
        </ActionIcon>
      </Group>

      <div className={styles.stage}>
        <Button
          ref={nodeRef}
          onPointerDown={isRipple ? startRipple : undefined}
          {...appearance.buttonProps}
          color={appearance.buttonProps.color || siteColor}
          ff={siteTheme?.fontFamily}
          aria-label={iconOnly ? text : undefined}
          leftSection={appearance.iconSide === 'left' && !iconOnly ? iconNode : undefined}
          rightSection={appearance.iconSide === 'right' && !iconOnly ? iconNode : undefined}
          className={appearance.className}
        >
          {iconOnly ? iconNode : text}
        </Button>
      </div>

      <Text size="10px" c="dimmed" ta="center" mt={6}>
        Survolez, cliquez ou tabulez jusqu’à l’aperçu.
      </Text>
    </Box>
  );
}

export function puckButtonPreviewField() {
  return {
    type: 'custom',
    label: 'Aperçu',
    render: () => <ButtonPreview />,
  };
}
